import axios from 'axios';
import { ApiContext } from 'api/api-context';
import { ActionName, actions, GetActionMap } from 'api/actions/actions';
import useLocalStorage from 'hooks/use-local-storage';
import { REST_API_TIMEOUT, REST_API_URL } from 'env';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useLang from 'lang';
import { useSearchParams } from 'react-router-dom';

/**
 * Parses a building ID from a URL parameter.
 */
function parseBuildingId(value: string | null): number | null {
  if (!value) {
    return null;
  }

  const parsedValue = Number(value);

  return Number.isSafeInteger(parsedValue) && parsedValue > 0 ? parsedValue : null;
}

/**
 * The session provider is used to provide the session context.
 */
export default function ApiProvider({ children }: { children: React.ReactNode }) {
  const { lang } = useLang();
  const [searchParams, setParams] = useSearchParams();
  const [ready, setReady] = useState(false);

  const [{ jwt }, setJwt] = useLocalStorage<{ jwt: string }>('phillip.session.jwt', {
    jwt: searchParams.get('jwt') || '',
  });

  const [{ buildingId }, setBuildingId] = useLocalStorage<{ buildingId: number | null }>('phillip.session.buildingId', {
    buildingId: parseBuildingId(searchParams.get('buildingId')),
  });

  /**
   * Clears the authenticated session and selected building.
   */
  const clearSession = useCallback(() => {
    setJwt({ jwt: '' });
    setBuildingId({ buildingId: null });
  }, [setBuildingId, setJwt]);

  /**
   * Starts an authenticated session.
   */
  const startSession = useCallback(
    (token: string) => {
      setBuildingId({ buildingId: null });
      setJwt({ jwt: token });
    },
    [setBuildingId, setJwt]
  );

  /**
   * Selects a building for the current session.
   */
  const selectBuilding = useCallback(
    (nextBuildingId: number | null) => {
      setBuildingId({ buildingId: nextBuildingId });
    },
    [setBuildingId]
  );

  /**
   * Creates a contextualized action.
   */
  const getAction: GetActionMap = useMemo(() => {
    const connector = axios.create({
      timeout: REST_API_TIMEOUT,
      baseURL: REST_API_URL,
    });

    /**
     * Adds JWT to the request.
     */
    connector.interceptors.request.use((cfg) => {
      if (jwt) {
        cfg.headers.Authorization = `Bearer ${jwt}`;
      }

      cfg.headers['Accept-Language'] = lang;

      return cfg;
    });

    connector.interceptors.response.use(
      (response) => response.data,
      (error) => {
        if (error.response?.status === 401) {
          clearSession();
        }

        return Promise.reject(error);
      }
    );

    const contextualizedActions = new Map(
      Array.from(actions.entries()).map(([name, createAction]) => [name, createAction(connector)])
    );

    return (action: ActionName) => contextualizedActions.get(action) as any;
  }, [jwt, lang, clearSession]);

  // Import session values from the URL, then remove them without changing the
  // current route or any unrelated query parameters.
  useEffect(() => {
    const jwtFromUrl = searchParams.get('jwt');
    const buildingIdFromUrl = searchParams.get('buildingId');

    if (jwtFromUrl) {
      setJwt({ jwt: jwtFromUrl });
    }

    if (buildingIdFromUrl !== null) {
      setBuildingId({ buildingId: parseBuildingId(buildingIdFromUrl) });
    }

    setParams(
      (previousParams) => {
        const nextParams = new URLSearchParams(previousParams);
        nextParams.delete('jwt');
        nextParams.delete('buildingId');
        return nextParams;
      },
      { replace: true }
    );
    localStorage.removeItem('buildingId');
    setReady(true);
  }, []);

  const value = useMemo(
    () => ({ jwt, buildingId, startSession, selectBuilding, clearSession, getAction }),
    [jwt, buildingId, startSession, selectBuilding, clearSession, getAction]
  );

  return <ApiContext.Provider value={value}>{ready ? children : <></>}</ApiContext.Provider>;
}
